import { computed, watch, onMounted, ref } from 'vue'

import { useContentBlockStore } from '../stores/content-block-store'
import { paragraphsToContentBlock } from '../utils/paragraphs-to-content-block'
import { type ContentBlock } from '../../utils/content-block-schema'
import { getDeviceData } from '../utils/get-device-data'
import { useClient } from '../../utils/client-only'

import type { AdPlacement } from '../../advertisement/types/ad-slot-types'

export function useContentBlockList (paragraphs: ContentBlock[], adPlacement?: AdPlacement | null) {
  const contentBlockStore = useContentBlockStore()
  const isHydrated = ref(false)

  // Initial setup on both server and client - without adPlacement for consistent hydration
  watch(paragraphs, (newParagraphs) => {
    const contentBlocks = paragraphsToContentBlock(newParagraphs)

    // Only apply adPlacement after hydration on client, or if forced in SSR for consistency
    if (isHydrated.value) {
      const deviceData = getDeviceData()
      contentBlockStore.setContentBlockList(contentBlocks, adPlacement, deviceData)
    } else {
      // For initial server rendering and pre-hydration client rendering, use undefined for consistent output
      contentBlockStore.setContentBlockList(contentBlocks, undefined, undefined)
    }
  }, { immediate: true })

  // Set hydration flag and update with adPlacement once component is mounted (client-only)
  onMounted(() => {
    isHydrated.value = true

    setTimeout(() => {
      // Re-apply content blocks with adPlacement after hydration is complete
      if (paragraphs.length > 0) {
        // Capture content blocks with the correct dimensions
        const whatWeAlreadyHave = contentBlockStore.contentBlockList
        const withoutAds = whatWeAlreadyHave.filter((block) => block.type !== '__injected_ad__')
        const deviceData = getDeviceData()
        contentBlockStore.setContentBlockList(withoutAds, adPlacement, deviceData)
      }
    }, 0)
  })

  const { isClient } = useClient()

  const isDebug = computed(() => {
    if (isClient.value) {
      const urlParams = new URLSearchParams(window.location.search)
      return urlParams.get('showAdSlotMarker') === 'true'
    }
    return false
  })

  const contentBlocks = computed(() => {
    if (isDebug.value) {
      return contentBlockStore.contentBlockList
    }
    return contentBlockStore.contentBlockList.filter((block) => block.enabled)
  })

  return {
    contentBlocks
  }
}
